import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Table } from "antd";

import { useEditableCell } from "../../hooks/useEditableTableCell.js";

import stateTaxRates from "./json/stateTaxRates.json";
import { emdash } from "../../utils/Constants/systemSetting.js";

export default function AwardsTable({
  companyInfo,
  awardTitle,
  paramTableRows,
  awardCalculatorValues,
  setAwardCalculatorValues,
  headless = false,
}) {
  const renderEditableCell = useEditableCell();

  // Initialization function to populate the first award's values
  useEffect(() => {
    if (!awardCalculatorValues?.length) {
      const getTaxDefaultValues = (label) =>
        Object.fromEntries(
          Object.values(paramTableRows).map((row) => [
            row.key,
            row.key === "ID"
              ? label
              : (row?.shortTermDefault || row?.shortTermDefault === 0) &&
                  label === "Short-Term"
                ? row?.shortTermDefault
                : (row?.longTermDefault || row?.longTermDefault === 0) &&
                    label === "Long-Term"
                  ? row?.longTermDefault
                  : "",
          ]),
        );
      setAwardCalculatorValues([
        getTaxDefaultValues("Short-Term"),
        getTaxDefaultValues("Long-Term"),
      ]);
    }
  }, [paramTableRows, awardCalculatorValues, setAwardCalculatorValues]);

  const [totalTaxUpdateNeeded, setTotalTaxUpdateNeeded] = useState(true);

  // Function for updating the options values
  const updateOptionParams = useCallback(
    (ID, record, newValue) => {
      const awardIndex = awardCalculatorValues.findIndex(
        (item) => item.ID === ID,
      );
      const newOptionsValues = [...awardCalculatorValues];
      const newOptionAwardValues = {
        ...awardCalculatorValues[awardIndex],
        [record.key]: newValue,
      };
      newOptionsValues[awardIndex] = newOptionAwardValues;

      setAwardCalculatorValues(newOptionsValues);
      setTotalTaxUpdateNeeded(true);
    },
    [setAwardCalculatorValues, awardCalculatorValues],
  );

  useEffect(() => {
    const getCurrentValue = (item) => {
      if (!isNaN(parseFloat(item))) {
        return parseFloat(item);
      }
      return 0;
    };
    if (totalTaxUpdateNeeded) {
      setAwardCalculatorValues((prev) => {
        const currentStateSelected = prev.find(
          (calculatorValues) => calculatorValues.ID === "Short-Term",
        )?.state;
        return prev.map((item) => {
          const currentStateTax = currentStateSelected ? (
            100 *
            parseFloat(
              stateTaxRates.find(
                (stateTaxDetails) =>
                  stateTaxDetails.State === currentStateSelected,
              )?.[item?.ID === "Short-Term" ? "Short-term" : "Long-term"],
            )
          ) : (
            <span style={{ color: "var(--dark-red)", fontWeight: "bold" }}>
              {emdash}
            </span>
          );

          return {
            ...item,
            stateTaxRate: currentStateTax,
            totalTax:
              getCurrentValue(currentStateTax) +
              getCurrentValue(item?.federalTaxRate) +
              getCurrentValue(item?.otherTax),
          };
        });
      });
      setTotalTaxUpdateNeeded(false);
    }
  }, [setAwardCalculatorValues, totalTaxUpdateNeeded]);

  // Set defintion of basic column(s)
  const defaultColumns = [
    {
      title: `${awardTitle} Parameter`,
      dataIndex: "key",
      key: "key",
      align: "center",
      render: (text, record) => record.title,
    },
  ];
  const [paramsColumns, setParamsColumns] = useState([...defaultColumns]);

  // Add/remove award columns based on the current values' updates.
  // Also initializes the first grant column based on awardCalculatorValues initialization above
  useMemo(() => {
    if (!awardCalculatorValues) return;

    setParamsColumns((prev) => {
      const currentAwards = awardCalculatorValues
        .filter(
          (award) =>
            !prev.some((existingAward) => award.ID === existingAward.key),
        )
        .map((award) => ({
          title: (
            <div>
              <p style={{ textAlign: "center" }}>{award.ID}</p>{" "}
              <p style={{ textAlign: "center" }}>Captital Gains</p>{" "}
            </div>
          ),
          key: `${award.ID}`,
          align: "center",
        }));
      return [...prev, ...currentAwards];
    });
  }, [awardCalculatorValues]);

  const [currentEditedCell, setCurrentEditedCell] = useState("");

  // Function to add a render function to each column (to try to keep the ref in memory current)
  const paramTableRowsRendered = useMemo(
    () =>
      paramsColumns.map((col) => {
        const currentAwardsValues = awardCalculatorValues?.find(
          (item) => item.ID === col.key,
        );

        if (col.key.includes("Term")) {
          col = {
            ...col,
            render: (text, record) => {
              const cellKey = `${record.key}-${col.key}`;
              const isCurrentEditedCell = currentEditedCell === cellKey;

              const currentAssignedValue = currentAwardsValues?.[record.key];

              const value =
                currentAssignedValue || currentAssignedValue === 0
                  ? currentAssignedValue
                  : isCurrentEditedCell
                    ? currentAssignedValue
                    : "";

              return renderEditableCell(
                value,
                record,
                col.key,
                record.dataType,
                updateOptionParams,
                setCurrentEditedCell,
              );
            },
          };
        }
        return col;
      }),
    [
      paramsColumns,
      updateOptionParams,
      renderEditableCell,
      awardCalculatorValues,
      currentEditedCell,
    ],
  );

  // Define the column parameters for the table (but actual data is controlled by state: awardCalculatorValues)
  const dataSource = Object.values(paramTableRows).map((row) => ({
    title: row.title,
    key: row.key,
    dataType: row.dataType,
    defaultValue:
      row?.defaultValue || row?.defaultValue === 0 ? row.defaultValue : "",
    isCalculatedValue: row?.isCalculatedValue || false,
    hideFromUser: row.hideFromUser,
  }));

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
      }}
    >
      <Table
        columns={paramTableRowsRendered}
        dataSource={dataSource}
        pagination={false}
        style={{ width: headless ? "65%" : "100%", marginBottom: 20 }}
        rowClassName={(record) =>
          record?.key === "ID" ? "hidden-row" : "no-hover"
        }
        size="small"
        showHeader={!headless}
      />
    </div>
  );
}
